import React, { useMemo } from 'react';
import {
    LayoutGrid,
    GitBranch,
    Target,
    Layers,
    Image,
    FlaskConical,
    Users,
    RefreshCw,
    CheckCircle2,
    AlertCircle
} from 'lucide-react';
import { useFunnelBuilder } from '../../contexts/FunnelBuilderContext';
import BudgetOverview from './BudgetOverview';

/**
 * FunnelSummaryPanel - Enhanced view shown when no node is selected
 * Includes BudgetOverview plus funnel metrics and summary
 */
const FunnelSummaryPanel = () => {
    const { nodes, edges, currentFunnel } = useFunnelBuilder();

    // Calculate funnel metrics
    const metrics = useMemo(() => {
        const nodesByType = {
            campaign: nodes.filter(n => n.type === 'campaign'),
            adset: nodes.filter(n => n.type === 'adset'),
            ad: nodes.filter(n => n.type === 'ad'),
            abtest: nodes.filter(n => n.type === 'abtest'),
            segment: nodes.filter(n => n.type === 'segment'),
            retargetingPool: nodes.filter(n => n.type === 'retargetingPool')
        };

        const nodesByStage = {
            TOFU: nodes.filter(n => n.data.funnelStage === 'TOFU'),
            MOFU: nodes.filter(n => n.data.funnelStage === 'MOFU'),
            BOFU: nodes.filter(n => n.data.funnelStage === 'BOFU')
        };

        // Check funnel completeness
        const completeness = {
            hasCampaign: nodesByType.campaign.length > 0,
            hasAdSet: nodesByType.adset.length > 0,
            hasAd: nodesByType.ad.length > 0,
            hasConnections: edges.length > 0,
            hasTOFU: nodesByStage.TOFU.length > 0,
            hasBOFU: nodesByStage.BOFU.length > 0
        };

        const completenessScore = Object.values(completeness).filter(Boolean).length;
        const maxScore = Object.keys(completeness).length;

        return {
            nodesByType,
            nodesByStage,
            completeness,
            completenessScore,
            maxScore,
            totalNodes: nodes.length,
            totalEdges: edges.length
        };
    }, [nodes, edges]);

    const typeIcons = {
        campaign: Target,
        adset: Layers,
        ad: Image,
        abtest: FlaskConical,
        segment: Users,
        retargetingPool: RefreshCw
    };

    const typeLabels = {
        campaign: 'Campaigns',
        adset: 'Ad Sets',
        ad: 'Ads',
        abtest: 'A/B Tests',
        segment: 'Segments',
        retargetingPool: 'Retargeting Pools'
    };

    const stageColors = {
        TOFU: 'var(--color-info)',
        MOFU: 'var(--color-warning)',
        BOFU: 'var(--color-success)'
    };

    return (
        <div style={{
            width: '360px',
            background: 'var(--color-surface)',
            borderLeft: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                padding: 'var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <LayoutGrid size={18} />
                    Funnel Summary
                </h3>
                {currentFunnel.name && (
                    <div style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                        marginTop: 'var(--space-1)'
                    }}>
                        {currentFunnel.name}
                    </div>
                )}
            </div>

            {/* Scrollable Content */}
            <div style={{
                flex: 1,
                overflowY: 'auto'
            }}>
                {/* Funnel Completeness */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <span style={{
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 600,
                            color: 'var(--color-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Funnel Health
                        </span>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: metrics.completenessScore === metrics.maxScore
                                ? 'var(--color-success)'
                                : metrics.completenessScore >= metrics.maxScore / 2
                                    ? 'var(--color-warning)'
                                    : 'var(--color-text-tertiary)'
                        }}>
                            {metrics.completenessScore}/{metrics.maxScore}
                        </span>
                    </div>

                    {/* Progress bar */}
                    <div style={{
                        height: '6px',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <div style={{
                            height: '100%',
                            width: `${(metrics.completenessScore / metrics.maxScore) * 100}%`,
                            background: metrics.completenessScore === metrics.maxScore
                                ? 'var(--color-success)'
                                : 'var(--color-primary)',
                            borderRadius: 'var(--radius-full)',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>

                    {/* Checklist */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {[
                            { key: 'hasCampaign', label: 'Has campaigns' },
                            { key: 'hasAdSet', label: 'Has ad sets' },
                            { key: 'hasAd', label: 'Has ads' },
                            { key: 'hasConnections', label: 'Nodes connected' },
                            { key: 'hasTOFU', label: 'Awareness stage' },
                            { key: 'hasBOFU', label: 'Conversion stage' }
                        ].map(item => (
                            <div key={item.key} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                fontSize: 'var(--font-size-sm)',
                                color: metrics.completeness[item.key]
                                    ? 'var(--color-text-primary)'
                                    : 'var(--color-text-tertiary)'
                            }}>
                                {metrics.completeness[item.key] ? (
                                    <CheckCircle2 size={14} style={{ color: 'var(--color-success)' }} />
                                ) : (
                                    <AlertCircle size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                                )}
                                {item.label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Node Counts by Type */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--color-text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: 'var(--space-3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <GitBranch size={12} />
                        Funnel Elements
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--space-2)'
                    }}>
                        {Object.entries(metrics.nodesByType).map(([type, typeNodes]) => {
                            const Icon = typeIcons[type];
                            return (
                                <div key={type} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-sm)'
                                }}>
                                    <Icon size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                                    <span style={{ flex: 1, color: 'var(--color-text-secondary)' }}>
                                        {typeLabels[type]}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{typeNodes.length}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Total connections */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: 'var(--space-3)',
                        padding: 'var(--space-2)',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                            Total Connections
                        </span>
                        <span style={{ fontWeight: 600 }}>{metrics.totalEdges}</span>
                    </div>
                </div>

                {/* Nodes by Stage */}
                <div style={{
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--color-text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: 'var(--space-3)'
                    }}>
                        By Funnel Stage
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {Object.entries(metrics.nodesByStage).map(([stage, stageNodes]) => (
                            <div key={stage} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)'
                            }}>
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '2px',
                                    background: stageColors[stage]
                                }} />
                                <span style={{
                                    flex: 1,
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    {stage}
                                </span>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600
                                }}>
                                    {stageNodes.length} nodes
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Budget Section - embed BudgetOverview content */}
                <BudgetOverview collapsed={false} embedded={true} />
            </div>

            {/* Tip */}
            {metrics.totalNodes === 0 && (
                <div style={{
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-tertiary)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                    textAlign: 'center'
                }}>
                    Drag nodes from the left panel to start building your funnel
                </div>
            )}
        </div>
    );
};

export default FunnelSummaryPanel;
